'use client';

/**
 * FooterMinimal - Minimal Footer for Auth/Simple Pages
 *
 * Features:
 * - Simple copyright only
 * - Optional legal links
 * - Compact design
 * - Branding-aware
 */

import React from 'react';
import Link from 'next/link';
import { useAgencyBranding } from '../AppShellContext';
import { COMPANY_NAME, ProductCompanyLockup } from '@/components/brand/EngageIQBrand';

// ============================================================================
// Types
// ============================================================================

interface FooterMinimalProps {
  /** Show legal links */
  showLegal?: boolean;

  /** Custom className */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function FooterMinimal({
  showLegal = true,
  className = '',
}: FooterMinimalProps) {
  const branding = useAgencyBranding();
  const currentYear = new Date().getFullYear();
  const companyName = branding?.company_name || COMPANY_NAME;

  return (
    <footer
      className={`py-4 px-4 ${className}`}
      style={{
        backgroundColor: 'transparent',
      }}
    >
      <div className="max-w-md mx-auto flex flex-col items-center gap-2">
        <ProductCompanyLockup productHeight={16} companyHeight={9} />

        {/* Copyright */}
        <p className="text-xs text-slate-500 text-center" suppressHydrationWarning>
          © {currentYear} {companyName}
        </p>

        {/* Legal Links */}
        {showLegal && (
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <a
              href="https://neutralaiz.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-400 transition"
            >
              Privacy
            </a>
            <span className="text-slate-700">•</span>
            <a
              href="https://neutralaiz.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-400 transition"
            >
              Terms
            </a>
            <span className="text-slate-700">•</span>
            <Link
              href="/accessibility"
              className="hover:text-slate-400 transition"
            >
              Accessibility
            </Link>
          </div>
        )}
      </div>
    </footer>
  );
}

// ============================================================================
// Citizen Footer Variant
// ============================================================================

interface FooterCitizenProps {
  /** Custom className */
  className?: string;
}

/**
 * Citizen portal footer with emerald accent
 */
export function FooterCitizen({ className = '' }: FooterCitizenProps) {
  const branding = useAgencyBranding();
  const currentYear = new Date().getFullYear();
  const companyName = branding?.company_name || COMPANY_NAME;

  return (
    <footer
      className={`py-6 px-4 border-t border-emerald-900/30 ${className}`}
      style={{
        backgroundColor: branding?.dark_background_color || 'rgba(0, 0, 0, 0.95)',
      }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Product and company attribution */}
          <div className="flex flex-col items-center gap-2 sm:items-start">
            <ProductCompanyLockup productHeight={16} companyHeight={9} />
            <p className="text-xs text-slate-500" suppressHydrationWarning>
              © {currentYear} {companyName}. Secure Citizen Portal.
            </p>
          </div>

          {/* Right: Links */}
          <div className="flex items-center gap-4 text-xs">
            <Link
              href="/citizen/help"
              className="text-emerald-400 hover:text-emerald-300 transition"
            >
              Help Center
            </Link>
            <a
              href="https://neutralaiz.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 hover:text-emerald-400 transition"
            >
              Privacy
            </a>
            <a
              href="https://neutralaiz.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 hover:text-emerald-400 transition"
            >
              Terms
            </a>
            <Link
              href="/accessibility"
              className="text-emerald-600 hover:text-emerald-400 transition"
            >
              Accessibility
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
// Developer Footer Variant
// ============================================================================

interface FooterDeveloperProps {
  /** Custom className */
  className?: string;
}

/**
 * Developer portal footer with violet accent
 */
export function FooterDeveloper({ className = '' }: FooterDeveloperProps) {
  const branding = useAgencyBranding();
  const currentYear = new Date().getFullYear();
  const companyName = branding?.company_name || COMPANY_NAME;

  return (
    <footer
      className={`py-6 px-4 border-t border-violet-900/30 ${className}`}
      style={{
        backgroundColor: branding?.dark_background_color || 'rgba(0, 0, 0, 0.95)',
      }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Product and company attribution */}
          <div className="flex flex-col items-center gap-2 sm:items-start">
            <ProductCompanyLockup productHeight={16} companyHeight={9} />
            <p className="text-xs text-slate-500" suppressHydrationWarning>
              © {currentYear} {companyName}. Developer Portal.
            </p>
          </div>

          {/* Right: Links */}
          <div className="flex items-center gap-4 text-xs">
            <Link
              href="/api-docs"
              className="text-violet-400 hover:text-violet-300 transition"
            >
              API Reference
            </Link>
            <a
              href="https://docs.neutralaiz.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:text-violet-400 transition"
            >
              Documentation
            </a>
            <a
              href="https://neutralaiz.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:text-violet-400 transition"
            >
              Privacy
            </a>
            <a
              href="https://neutralaiz.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:text-violet-400 transition"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default FooterMinimal;
